import { useId } from 'react';
import { DateInput, Input, Select, TimeInput } from '@/shared/ui';
import type { CampoPropostaMia } from './assistenteService';
import styles from './PropostaMia.module.css';

type Props = {
  campos: CampoPropostaMia[];
  valores: Record<string, string>;
  onChange: (chave: string, valor: string) => void;
  desabilitado: boolean;
};

function idsDe(valor: string): string[] {
  return valor
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

/** Edicao do cartao da proposta: um controle por tipo de campo (R21). */
export function CamposPropostaMia({ campos, valores, onChange, desabilitado }: Props) {
  const base = useId();
  return (
    <div className={styles.edicao}>
      {campos.map((campo) => {
        const valor = valores[campo.chave] ?? '';
        const id = `${base}-${campo.chave}`;
        switch (campo.tipo) {
          case 'hora':
            return (
              <TimeInput
                key={campo.chave}
                id={id}
                label={campo.rotulo}
                value={valor}
                disabled={desabilitado}
                onChange={(novo) => onChange(campo.chave, novo)}
              />
            );
          case 'data':
            return (
              <DateInput
                key={campo.chave}
                id={id}
                label={campo.rotulo}
                value={valor}
                disabled={desabilitado}
                onChange={(iso) => onChange(campo.chave, iso)}
              />
            );
          case 'opcao':
            return (
              <Select
                key={campo.chave}
                id={id}
                label={campo.rotulo}
                value={valor}
                disabled={desabilitado}
                options={campo.opcoes.map((o) => ({ value: o.valor, label: o.rotulo }))}
                onChange={(evento) => onChange(campo.chave, evento.target.value)}
              />
            );
          case 'selecao':
            return (
              <SelecaoCampo
                key={campo.chave}
                campo={campo}
                valor={valor}
                desabilitado={desabilitado}
                onChange={(novo) => onChange(campo.chave, novo)}
              />
            );
          case 'textoLongo':
            return (
              <div key={campo.chave} className={styles.campoLongo}>
                <label htmlFor={id}>{campo.rotulo}</label>
                <textarea
                  id={id}
                  rows={3}
                  maxLength={campo.max}
                  value={valor}
                  disabled={desabilitado}
                  onChange={(evento) => onChange(campo.chave, evento.target.value)}
                />
              </div>
            );
          default:
            return (
              <Input
                key={campo.chave}
                id={id}
                label={campo.rotulo}
                value={valor}
                maxLength={campo.max}
                disabled={desabilitado}
                onChange={(evento) => onChange(campo.chave, evento.target.value)}
              />
            );
        }
      })}
    </div>
  );
}

type SelecaoProps = {
  campo: CampoPropostaMia;
  valor: string;
  desabilitado: boolean;
  onChange: (valor: string) => void;
};

function SelecaoCampo({ campo, valor, desabilitado, onChange }: SelecaoProps) {
  const marcados = new Set(idsDe(valor));
  const todos = campo.opcoes.length > 0 && campo.opcoes.every((o) => marcados.has(o.valor));

  function alternar(id: string) {
    const proximo = new Set(marcados);
    if (proximo.has(id)) proximo.delete(id);
    else proximo.add(id);
    onChange(campo.opcoes.filter((o) => proximo.has(o.valor)).map((o) => o.valor).join(','));
  }

  return (
    <fieldset className={styles.selecao} disabled={desabilitado}>
      <legend>
        {campo.rotulo} ({marcados.size} de {campo.opcoes.length})
      </legend>
      <label className={styles.opcao}>
        <input
          type="checkbox"
          checked={todos}
          onChange={() => onChange(todos ? '' : campo.opcoes.map((o) => o.valor).join(','))}
        />
        Marcar todos
      </label>
      <div className={styles.opcoes}>
        {campo.opcoes.map((o) => (
          <label key={o.valor} className={styles.opcao}>
            <input type="checkbox" checked={marcados.has(o.valor)} onChange={() => alternar(o.valor)} />
            {o.rotulo}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
